import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Building2, MapPin, Mail, Phone, User, Lock, Globe, Palette,
  DollarSign, CheckCircle, AlertCircle, Loader2, ArrowRight, ArrowLeft,
  Handshake, Shield, Eye, EyeOff, Languages
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AgencyRegisterPage() {
  const { language, toggleLanguage } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const invitationCode = searchParams.get('code');
  
  // States
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    // Agency Info
    name: '',
    slug: '',
    description: '',
    city: '',
    region: 'Québec',
    
    // Currency
    currency_name: 'Dollar Local',
    currency_symbol: '$',
    currency_code: 'LOC',
    
    // Contact
    contact_email: '',
    contact_phone: '',
    contact_address: '',
    
    // Admin account
    admin_name: '',
    admin_email: '',
    admin_password: '',
    admin_password_confirm: '',
    
    // Branding
    primary_color: '#003DA5',
    background_image: '',
    
    // Links
    website_url: '',
    facebook_url: '',
    instagram_url: '',
    linkedin_url: '',
    twitter_url: ''
  });
  
  // Translations
  const t = {
    fr: {
      title: "Créer votre Agence Locale",
      subtitle: "Rejoignez le réseau PayHub Québec+",
      invalidCode: "Code d'invitation invalide ou expiré",
      noCode: "Code d'invitation manquant",
      step1: "Informations de l'agence",
      step2: "Contact & Liens",
      step3: "Compte Administrateur",
      step4: "Confirmation",
      
      // Step 1
      agencyName: "Nom de l'agence",
      agencyNamePlaceholder: "Ex: Caisse Locale de Montréal",
      slug: "Identifiant URL",
      slugHelp: "Votre agence sera accessible à: /agency/{slug}",
      description: "Description",
      descriptionPlaceholder: "Décrivez votre agence en quelques mots...",
      city: "Ville",
      cityPlaceholder: "Montréal",
      region: "Région",
      backgroundImage: "Image de fond (URL)",
      backgroundImagePlaceholder: "https://exemple.com/image.jpg",
      backgroundImageHelp: "URL d'une image pour le header de votre page",
      
      // Step 2
      contactInfo: "Coordonnées",
      contactEmail: "Email de contact",
      contactPhone: "Téléphone",
      contactAddress: "Adresse",
      currencyInfo: "Devise locale",
      currencyName: "Nom de la devise",
      currencySymbol: "Symbole",
      currencyCode: "Code",
      linksInfo: "Présence en ligne",
      websiteUrl: "Site web",
      facebookUrl: "Facebook",
      instagramUrl: "Instagram",
      linkedinUrl: "LinkedIn",
      twitterUrl: "X (Twitter)",
      
      // Step 3
      adminAccount: "Compte administrateur",
      adminName: "Votre nom complet",
      adminEmail: "Votre email",
      adminPassword: "Mot de passe",
      adminPasswordConfirm: "Confirmer le mot de passe",
      passwordRequirements: "Minimum 8 caractères",
      passwordMismatch: "Les mots de passe ne correspondent pas",
      
      // Step 4
      reviewTitle: "Vérifiez vos informations",
      agencyInfo: "Agence",
      contactDetails: "Contact",
      adminInfo: "Administrateur",
      socialLinks: "Liens",
      
      // Buttons
      next: "Suivant",
      back: "Retour",
      submit: "Créer mon agence",
      creating: "Création en cours...",
      
      // Success
      successTitle: "Agence créée avec succès!",
      successMessage: "Votre agence est maintenant active. Connectez-vous avec vos identifiants.",
      goToLogin: "Se connecter",
      
      // Welcome message
      welcomeTitle: "Bienvenue!",
      welcomeMessage: "Vous avez été invité à créer une agence locale dans le réseau PayHub Québec+.",
      invitedAgency: "Agence suggérée:",
      
      // Validation
      required: "Ce champ est requis",
      invalidEmail: "Email invalide"
    },
    en: {
      title: "Create Your Local Agency",
      subtitle: "Join the PayHub Québec+ Network",
      invalidCode: "Invalid or expired invitation code",
      noCode: "Missing invitation code",
      step1: "Agency Information",
      step2: "Contact & Links",
      step3: "Administrator Account",
      step4: "Confirmation",
      
      // Step 1
      agencyName: "Agency Name",
      agencyNamePlaceholder: "Ex: Montreal Local Credit Union",
      slug: "URL Identifier",
      slugHelp: "Your agency will be accessible at: /agency/{slug}",
      description: "Description",
      descriptionPlaceholder: "Describe your agency in a few words...",
      city: "City",
      cityPlaceholder: "Montreal",
      region: "Region",
      backgroundImage: "Background Image (URL)",
      backgroundImagePlaceholder: "https://example.com/image.jpg",
      backgroundImageHelp: "URL of an image for your page header",
      
      // Step 2
      contactInfo: "Contact Information",
      contactEmail: "Contact Email",
      contactPhone: "Phone",
      contactAddress: "Address",
      currencyInfo: "Local Currency",
      currencyName: "Currency Name",
      currencySymbol: "Symbol",
      currencyCode: "Code",
      linksInfo: "Online Presence",
      websiteUrl: "Website",
      facebookUrl: "Facebook",
      instagramUrl: "Instagram",
      linkedinUrl: "LinkedIn",
      twitterUrl: "X (Twitter)",
      
      // Step 3
      adminAccount: "Administrator Account",
      adminName: "Your Full Name",
      adminEmail: "Your Email",
      adminPassword: "Password",
      adminPasswordConfirm: "Confirm Password",
      passwordRequirements: "Minimum 8 characters",
      passwordMismatch: "Passwords do not match",
      
      // Step 4
      reviewTitle: "Review Your Information",
      agencyInfo: "Agency",
      contactDetails: "Contact",
      adminInfo: "Administrator",
      socialLinks: "Links",
      
      // Buttons
      next: "Next",
      back: "Back",
      submit: "Create My Agency",
      creating: "Creating...",
      
      // Success
      successTitle: "Agency Created Successfully!",
      successMessage: "Your agency is now active. Log in with your credentials.",
      goToLogin: "Log In",
      
      // Welcome message
      welcomeTitle: "Welcome!",
      welcomeMessage: "You have been invited to create a local agency in the PayHub Québec+ network.",
      invitedAgency: "Suggested agency:",
      
      // Validation
      required: "This field is required",
      invalidEmail: "Invalid email"
    }
  };
  
  const text = t[language] || t.fr;
  
  // Validate invitation code on mount
  useEffect(() => {
    async function validateCode() {
      if (!invitationCode) {
        setError(text.noCode);
        setLoading(false);
        return;
      }
      
      try {
        const res = await fetch(`${API_URL}/api/agencies/invitations/validate/${invitationCode}`);
        const data = await res.json();
        
        if (!res.ok) {
          throw new Error(data.detail || text.invalidCode);
        }
        
        setInvitation(data);
        
        // Pre-fill form with invitation data
        setFormData(prev => ({
          ...prev,
          name: data.agency_name || '',
          admin_email: data.email || '',
          contact_email: data.email || ''
        }));
        
      } catch (err) {
        setError(err.message || text.invalidCode);
      } finally {
        setLoading(false);
      }
    }
    
    validateCode();
  }, [invitationCode, text.invalidCode, text.noCode]);
  
  // Generate slug from name
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };
  
  // Handle input changes
  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-generate slug from name
      if (field === 'name') {
        updated.slug = generateSlug(value);
      }
      
      return updated;
    });
  };
  
  // Validate current step
  const validateStep = () => {
    switch (step) {
      case 1:
        return formData.name && formData.slug && formData.city;
      case 2:
        return formData.contact_email && formData.currency_name;
      case 3:
        return (
          formData.admin_name && 
          formData.admin_email && 
          formData.admin_password && 
          formData.admin_password.length >= 8 &&
          formData.admin_password === formData.admin_password_confirm
        );
      default:
        return true;
    }
  };
  
  // Submit form
  const handleSubmit = async () => {
    setSubmitting(true);
    
    try {
      const payload = {
        name: formData.name,
        slug: formData.slug,
        description: formData.description,
        city: formData.city,
        region: formData.region,
        currency_name: formData.currency_name,
        currency_symbol: formData.currency_symbol,
        currency_code: formData.currency_code,
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone,
        contact_address: formData.contact_address,
        admin_name: formData.admin_name,
        admin_email: formData.admin_email,
        admin_password: formData.admin_password,
        invitation_code: invitationCode,
        // New fields
        background_image: formData.background_image,
        website_url: formData.website_url,
        facebook_url: formData.facebook_url,
        instagram_url: formData.instagram_url,
        linkedin_url: formData.linkedin_url,
        twitter_url: formData.twitter_url
      };
      
      const res = await fetch(`${API_URL}/api/agencies/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Registration failed');
      }
      
      // Success!
      setStep(5);
      toast.success(text.successTitle);
      
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-pink-500 mx-auto mb-4" />
          <p className="text-muted-foreground">{language === 'fr' ? 'Validation...' : 'Validating...'}</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold mb-2">{language === 'fr' ? 'Erreur' : 'Error'}</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate('/')} variant="outline">
              {language === 'fr' ? 'Retour à l\'accueil' : 'Back to Home'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Success state
  if (step === 5) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold mb-2">{text.successTitle}</h2>
            <p className="text-muted-foreground mb-6">{text.successMessage}</p>
            <div className="p-4 bg-muted rounded-xl mb-6">
              <p className="text-sm text-muted-foreground mb-1">{language === 'fr' ? 'Votre URL d\'agence:' : 'Your agency URL:'}</p>
              <code className="font-mono text-primary">/agency/{formData.slug}</code>
            </div>
            <Button onClick={() => navigate('/login')} className="w-full bg-gradient-to-r from-pink-500 to-rose-600">
              {text.goToLogin}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-blue-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 text-white mb-4">
            <Handshake className="h-8 w-8" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{text.title}</h1>
          
          {/* Subtitle with Language Toggle */}
          <div className="flex items-center justify-center gap-3 mt-2">
            <p className="text-muted-foreground">{text.subtitle}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleLanguage}
              className="bg-white shadow-sm hover:bg-gray-50 border-pink-200 text-pink-600 font-medium"
            >
              <Languages className="h-4 w-4 mr-1.5" />
              {language === 'fr' ? 'EN' : 'FR'}
            </Button>
          </div>
        </div>
        
        {/* Welcome Card (if invitation has message) */}
        {invitation?.message && step === 1 && (
          <Card className="mb-6 border-pink-200 bg-pink-50/50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                  <Mail className="h-5 w-5 text-pink-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-pink-900">{text.welcomeTitle}</h3>
                  <p className="text-sm text-pink-700 mt-1">{text.welcomeMessage}</p>
                  {invitation.agency_name && (
                    <p className="text-sm mt-2">
                      <span className="text-pink-600">{text.invitedAgency}</span>{' '}
                      <strong>{invitation.agency_name}</strong>
                    </p>
                  )}
                  {invitation.message && (
                    <p className="text-sm text-pink-600 italic mt-2">"{invitation.message}"</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3, 4].map((s) => (
            <React.Fragment key={s}>
              <div 
                className={`h-8 w-8 sm:h-10 sm:w-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  s === step 
                    ? 'bg-gradient-to-r from-pink-500 to-rose-600 text-white' 
                    : s < step 
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                }`}
              >
                {s < step ? <CheckCircle className="h-4 w-4" /> : s}
              </div>
              {s < 4 && (
                <div className={`h-1 w-8 sm:w-12 rounded ${s < step ? 'bg-green-500' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
        
        {/* Form Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              {step === 1 && <Building2 className="h-5 w-5 text-pink-500" />}
              {step === 2 && <Globe className="h-5 w-5 text-pink-500" />}
              {step === 3 && <User className="h-5 w-5 text-pink-500" />}
              {step === 4 && <Shield className="h-5 w-5 text-pink-500" />}
              {step === 1 && text.step1}
              {step === 2 && text.step2}
              {step === 3 && text.step3}
              {step === 4 && text.step4}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Step 1: Agency Info */}
            {step === 1 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name">{text.agencyName} *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder={text.agencyNamePlaceholder}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="slug">{text.slug}</Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) => handleChange('slug', e.target.value)}
                    className="font-mono"
                  />
                  <p className="text-xs text-muted-foreground">
                    {text.slugHelp.replace('{slug}', formData.slug || 'votre-agence')}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">{text.description}</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder={text.descriptionPlaceholder}
                    rows={3}
                  />
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">{text.city} *</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => handleChange('city', e.target.value)}
                        placeholder={text.cityPlaceholder}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="region">{text.region}</Label>
                    <Input
                      id="region"
                      value={formData.region}
                      onChange={(e) => handleChange('region', e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="background_image">{text.backgroundImage}</Label>
                  <Input
                    id="background_image"
                    type="url"
                    value={formData.background_image}
                    onChange={(e) => handleChange('background_image', e.target.value)}
                    placeholder={text.backgroundImagePlaceholder}
                  />
                  <p className="text-xs text-muted-foreground">{text.backgroundImageHelp}</p>
                  {formData.background_image && (
                    <div className="mt-2 rounded-lg overflow-hidden h-24 bg-cover bg-center border" 
                         style={{ backgroundImage: `url(${formData.background_image})` }}>
                    </div>
                  )}
                </div>
              </>
            )}
            
            {/* Step 2: Contact & Currency */}
            {step === 2 && (
              <>
                <div className="space-y-4">
                  <h4 className="font-medium flex items-center gap-2">
                    <Phone className="h-4 w-4 text-pink-500" />
                    {text.contactInfo}
                  </h4>
                  
                  <div className="space-y-2">
                    <Label htmlFor="contact_email">{text.contactEmail} *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="contact_email"
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => handleChange('contact_email', e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contact_phone">{text.contactPhone}</Label>
                      <Input
                        id="contact_phone"
                        value={formData.contact_phone}
                        onChange={(e) => handleChange('contact_phone', e.target.value)}
                        placeholder="514-555-1234"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact_address">{text.contactAddress}</Label>
                      <Input
                        id="contact_address"
                        value={formData.contact_address}
                        onChange={(e) => handleChange('contact_address', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="border-t pt-6 space-y-4">
                  <h4 className="font-medium flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-pink-500" />
                    {text.currencyInfo}
                  </h4>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2 sm:col-span-1">
                      <Label htmlFor="currency_name">{text.currencyName}</Label>
                      <Input
                        id="currency_name"
                        value={formData.currency_name}
                        onChange={(e) => handleChange('currency_name', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="currency_symbol">{text.currencySymbol}</Label>
                      <Input
                        id="currency_symbol"
                        value={formData.currency_symbol}
                        onChange={(e) => handleChange('currency_symbol', e.target.value)}
                        className="text-center font-bold"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="currency_code">{text.currencyCode}</Label>
                      <Input
                        id="currency_code"
                        value={formData.currency_code}
                        onChange={(e) => handleChange('currency_code', e.target.value.toUpperCase())}
                        className="text-center font-mono"
                        maxLength={3}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="border-t pt-6 space-y-4">
                  <h4 className="font-medium flex items-center gap-2">
                    <Globe className="h-4 w-4 text-pink-500" />
                    {text.linksInfo}
                  </h4>
                  
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="website_url">{text.websiteUrl}</Label>
                      <Input
                        id="website_url"
                        type="url"
                        value={formData.website_url}
                        onChange={(e) => handleChange('website_url', e.target.value)}
                        placeholder="https://votre-site.com"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="facebook_url">{text.facebookUrl}</Label>
                        <Input
                          id="facebook_url"
                          type="url"
                          value={formData.facebook_url}
                          onChange={(e) => handleChange('facebook_url', e.target.value)}
                          placeholder="https://facebook.com/..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="instagram_url">{text.instagramUrl}</Label>
                        <Input
                          id="instagram_url"
                          type="url"
                          value={formData.instagram_url}
                          onChange={(e) => handleChange('instagram_url', e.target.value)}
                          placeholder="https://instagram.com/..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="linkedin_url">{text.linkedinUrl}</Label>
                        <Input
                          id="linkedin_url"
                          type="url"
                          value={formData.linkedin_url}
                          onChange={(e) => handleChange('linkedin_url', e.target.value)}
                          placeholder="https://linkedin.com/company/..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="twitter_url">{text.twitterUrl}</Label>
                        <Input
                          id="twitter_url"
                          type="url"
                          value={formData.twitter_url}
                          onChange={(e) => handleChange('twitter_url', e.target.value)}
                          placeholder="https://x.com/..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
            
            {/* Step 3: Admin Account */}
            {step === 3 && (
              <>
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 mb-4">
                  <p className="text-sm text-blue-700">
                    <Shield className="h-4 w-4 inline mr-2" />
                    {language === 'fr' 
                      ? 'Ce compte sera l\'administrateur principal de votre agence.'
                      : 'This account will be the main administrator of your agency.'}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="admin_name">{text.adminName} *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="admin_name"
                      value={formData.admin_name}
                      onChange={(e) => handleChange('admin_name', e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="admin_email">{text.adminEmail} *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="admin_email"
                      type="email"
                      value={formData.admin_email}
                      onChange={(e) => handleChange('admin_email', e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="admin_password">{text.adminPassword} *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="admin_password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.admin_password}
                      onChange={(e) => handleChange('admin_password', e.target.value)}
                      className="pl-10 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">{text.passwordRequirements}</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="admin_password_confirm">{text.adminPasswordConfirm} *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="admin_password_confirm"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.admin_password_confirm}
                      onChange={(e) => handleChange('admin_password_confirm', e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  {formData.admin_password_confirm && formData.admin_password !== formData.admin_password_confirm && (
                    <p className="text-xs text-red-500">{text.passwordMismatch}</p>
                  )}
                </div>
              </>
            )}
            
            {/* Step 4: Review */}
            {step === 4 && (
              <div className="space-y-6">
                <p className="text-muted-foreground">{text.reviewTitle}</p>
                
                <div className="space-y-4">
                  <div className="p-4 bg-muted/50 rounded-xl">
                    <h4 className="font-medium flex items-center gap-2 mb-3">
                      <Building2 className="h-4 w-4 text-pink-500" />
                      {text.agencyInfo}
                    </h4>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">{text.agencyName}:</dt>
                        <dd className="font-medium">{formData.name}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">URL:</dt>
                        <dd className="font-mono text-xs">/agency/{formData.slug}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">{text.city}:</dt>
                        <dd>{formData.city}, {formData.region}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">{text.currencyName}:</dt>
                        <dd>{formData.currency_name} ({formData.currency_symbol})</dd>
                      </div>
                    </dl>
                  </div>
                  
                  <div className="p-4 bg-muted/50 rounded-xl">
                    <h4 className="font-medium flex items-center gap-2 mb-3">
                      <Phone className="h-4 w-4 text-pink-500" />
                      {text.contactDetails}
                    </h4>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">Email:</dt>
                        <dd>{formData.contact_email}</dd>
                      </div>
                      {formData.contact_phone && (
                        <div className="flex justify-between">
                          <dt className="text-muted-foreground">{text.contactPhone}:</dt>
                          <dd>{formData.contact_phone}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                  
                  <div className="p-4 bg-muted/50 rounded-xl">
                    <h4 className="font-medium flex items-center gap-2 mb-3">
                      <User className="h-4 w-4 text-pink-500" />
                      {text.adminInfo}
                    </h4>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">{text.adminName}:</dt>
                        <dd>{formData.admin_name}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">Email:</dt>
                        <dd>{formData.admin_email}</dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </div>
            )}
            
            {/* Navigation Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
              {step > 1 && (
                <Button 
                  variant="outline" 
                  onClick={() => setStep(step - 1)}
                  className="w-full sm:w-auto"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {text.back}
                </Button>
              )}
              
              <div className="flex-1" />
              
              {step < 4 ? (
                <Button 
                  onClick={() => setStep(step + 1)}
                  disabled={!validateStep()}
                  className="w-full sm:w-auto bg-gradient-to-r from-pink-500 to-rose-600"
                >
                  {text.next}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full sm:w-auto bg-gradient-to-r from-pink-500 to-rose-600"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {text.creating}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      {text.submit}
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Footer */}
        <p className="text-center text-sm text-muted-foreground mt-8">
          ⚜️ PayHub Québec+ — {language === 'fr' ? 'Soutenons les monnaies locales du Québec!' : 'Supporting Quebec\'s local currencies!'}
        </p>
      </div>
    </div>
  );
}
